import { Injectable } from '@angular/core';
import { FlashMessageService, FlashMessageType } from '@libs/shared/common';
import { ShopErrorItemModel } from '@libs/shop/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { StatusCodes } from 'http-status-codes';
import { catchError, filter, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';
import { AddConfigurableBundleToCartActionData, AddToCartActionData, CartQueueItem, ChangeQuantityActionData, RemoveActionData } from '..';
import { CartRepositoryService } from '../../../cart';
import { ShopErrorParserService } from '../../../shop-error-parser';
import * as CartActions from '../actions/cart-state.actions';
import { CartStateSelectors } from '../selectors';

@Injectable()
export class CartStateEffects {
  private static readonly MAX_CART_NOT_FOUND_RETRIES: number = 5;

  public triggerQueueProcessing$: Observable<Action<string>> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.addToCartQueue),
      withLatestFrom(this.store.pipe(select(CartStateSelectors.selectIsCartQueueProcessing))),
      filter(([, processing]) => !processing),
      map(() => {
        return CartActions.processCartQueue();
      }),
    ),
  );

  public cartAction$: Observable<Action<string>> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.processCartQueue),
      withLatestFrom(
        this.store.pipe(select(CartStateSelectors.selectCartQueue)),
      ),
      filter(([, queue]) => queue.length > 0),
      switchMap(([, queue]) => {
        const queueItem: CartQueueItem = queue[0];

        return this.executeCartAction(queueItem);
      }),
    ),
  );

  public queueProcessing$: Observable<Action<string>> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.cartActionSuccess, CartActions.cartActionFailure),
      withLatestFrom(
        this.store.pipe(select(CartStateSelectors.selectCartQueue)),
      ),
      map(([_, queue]) => {
        if (queue.length > 0) {
          return CartActions.processCartQueue();
        }

        return CartActions.cartQueueFinished();
      }),
    ),
  );

  public constructor(
    public readonly actions$: Actions,
    private readonly store: Store,
    private readonly cartRepository: CartRepositoryService,
    private readonly shopErrorParser: ShopErrorParserService,
    private readonly messageService: FlashMessageService,
  ) {
  }

  private executeCartAction(queueItem: CartQueueItem, retryCount: number = 0): Observable<Action<string>> {
    switch (queueItem.actionType) {
      case 'add':
        return this.executeAddToCart(queueItem, retryCount);
      case 'changeQuantity':
        return this.executeChangeQuantity(queueItem, retryCount);
      case 'remove':
        return this.executeRemoveFromCart(queueItem, retryCount);
      case 'addConfigurableBundle':
        return this.executeAddConfigurableBundleToCart(queueItem, retryCount);
    }
  }

  private executeAddToCart(queueItem: CartQueueItem, retryCount: number): Observable<Action<string>> {
    const actionData: AddToCartActionData = queueItem.data as AddToCartActionData;

    return this.cartRepository.addItem(actionData.sku, actionData.quantity).pipe(
      take(1),
      map(() => {
        this.messageService.showMessage('successAddToCart');

        return CartActions.cartActionSuccess({ queueItem });
      }),
      catchError((error) => this.handleError(error, queueItem, retryCount, 'errorAddToCart')),
    );
  }

  private executeAddConfigurableBundleToCart(queueItem: CartQueueItem, retryCount: number): Observable<Action<string>> {
    const actionData: AddConfigurableBundleToCartActionData = queueItem.data as AddConfigurableBundleToCartActionData;

    return this.cartRepository.addConfigurableBundle(actionData.data).pipe(
      take(1),
      map(() => {
        this.messageService.showMessage('successAddToCart');

        return CartActions.cartActionSuccess({ queueItem });
      }),
      catchError((error) => this.handleError(error, queueItem, retryCount, 'errorAddToCart')),
    );
  }

  private executeChangeQuantity(queueItem: CartQueueItem, retryCount: number): Observable<Action<string>> {
    const actionData: ChangeQuantityActionData = queueItem.data as ChangeQuantityActionData;

    return this.cartRepository.updateItem(actionData.cartItem).pipe(
      take(1),
      map(() => {
        this.messageService.showMessage('successChangeQuantity');

        return CartActions.cartActionSuccess({ queueItem });
      }),
      catchError((error) => this.handleError(error, queueItem, retryCount, 'errorChangeQuantity')),
    );
  }

  private executeRemoveFromCart(queueItem: CartQueueItem, retryCount: number): Observable<Action<string>> {
    const actionData: RemoveActionData = queueItem.data as RemoveActionData;

    return this.cartRepository.removeItem(actionData.cartItem).pipe(
      take(1),
      map(() => {
        this.messageService.showMessage('successRemoveFromCart');

        return CartActions.cartActionSuccess({ queueItem });
      }),
      catchError((error) => this.handleError(error, queueItem, retryCount, 'errorRemoveFromCart')),
    );
  }

  private handleError(error: unknown, queueItem: CartQueueItem, retryCount: number, unknownErrorKey: FlashMessageType): Observable<Action<string>> {
    const errors: ShopErrorItemModel[] = this.shopErrorParser.tryParseErrors(error);
    const isCartNotFound: boolean = errors.some(x => x.status === Number(StatusCodes.NOT_FOUND));

    if (isCartNotFound && retryCount < CartStateEffects.MAX_CART_NOT_FOUND_RETRIES) {
      return this.cartRepository.initCartId().pipe(
        take(1),
        switchMap(() => this.executeCartAction(queueItem, retryCount + 1)),
        catchError((initError: unknown) => {
          this.shopErrorParser.parseAndShowRawErrorMessages(initError, unknownErrorKey);

          return of(CartActions.cartActionFailure({ queueItem }));
        }),
      );
    }

    this.shopErrorParser.parseAndShowRawErrorMessages(error, unknownErrorKey);

    return of(CartActions.cartActionFailure({ queueItem }));
  }
}
